/* istanbul ignore file */
/* tslint:disable */
 
import type { APIEntity } from '../models/APIEntity';
import type { APILegalEntityDetail } from '../models/APILegalEntityDetail';
import type { APIPersonDetail } from '../models/APIPersonDetail';
import type { Color } from '../models/Color';
import type { Entity } from '../models/Entity';
import type { FieldValidationOutput } from '../models/FieldValidationOutput';
import type { FieldValidationRequest } from '../models/FieldValidationRequest';
import type { LegalEntity } from '../models/LegalEntity';
import type { LegalEntityDetail } from '../models/LegalEntityDetail';
import type { PaginatedAPIEntityList } from '../models/PaginatedAPIEntityList';
import type { PaginatedAPILegalEntityListList } from '../models/PaginatedAPILegalEntityListList';
import type { PaginatedAPIPersonListList } from '../models/PaginatedAPIPersonListList';
import type { PaginatedColorList } from '../models/PaginatedColorList';
import type { PaginatedEntityList } from '../models/PaginatedEntityList';
import type { PaginatedLegalEntityListList } from '../models/PaginatedLegalEntityListList';
import type { PaginatedPersonListList } from '../models/PaginatedPersonListList';
import type { PaginatedPropertyListList } from '../models/PaginatedPropertyListList';
import type { PaginatedUserLegalEntityList } from '../models/PaginatedUserLegalEntityList';
import type { PaginatedUserPersonList } from '../models/PaginatedUserPersonList';
import type { PaginatedUserVehicleList } from '../models/PaginatedUserVehicleList';
import type { PaginatedVehicleBrandList } from '../models/PaginatedVehicleBrandList';
import type { PaginatedVehicleListList } from '../models/PaginatedVehicleListList';
import type { PaginatedVehicleModelList } from '../models/PaginatedVehicleModelList';
import type { PatchedColor } from '../models/PatchedColor';
import type { PatchedLegalEntity } from '../models/PatchedLegalEntity';
import type { PatchedPerson } from '../models/PatchedPerson';
import type { PatchedUserPerson } from '../models/PatchedUserPerson';
import type { PatchedVehicle } from '../models/PatchedVehicle';
import type { PatchedVehicleBrand } from '../models/PatchedVehicleBrand';
import type { PatchedVehicleModel } from '../models/PatchedVehicleModel';
import type { Person } from '../models/Person';
import type { PersonDetail } from '../models/PersonDetail';
import type { PropertyDetail } from '../models/PropertyDetail';
import type { UserLegalEntity } from '../models/UserLegalEntity';
import type { UserPerson } from '../models/UserPerson';
import type { UserVehicle } from '../models/UserVehicle';
import type { Vehicle } from '../models/Vehicle';
import type { VehicleBrand } from '../models/VehicleBrand';
import type { VehicleDetail } from '../models/VehicleDetail';
import type { VehicleModel } from '../models/VehicleModel';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class EntitiesService {

  /**
   * Listare entități
   * Returnează catalogul unificat de entități: persoane fizice, entități juridice și vehicule. Acest endpoint este util atunci când integratorul are nevoie de o căutare generală, indiferent de tipul entității.
   * @param contentType Filtrează entitățile după tip: persoană, entitate juridică sau vehicul.
 *
 * * `person` - Person
 * * `legalentity` - Legal Entity
 * * `vehicle` - Vehicle
 * * `property` - Property
 * * `cmrrisk` - Cmr Risk
   * @param excludeIds comma separated list
   * @param identifier Potrivire exactă pentru IDNP, IDNO sau numărul certificatului vehiculului.
   * @param ids comma separated list
   * @param ordering Ordering fields: id, object__name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: as_person__first_name, as_person__last_name, as_person__identification_number, as_legalentity__name, as_legalentity__identification_number, as_vehicle__model__name, as_vehicle__brand__name, as_vehicle__certificate_number, as_vehicle__registration_number, as_property__description
   * @returns PaginatedAPIEntityList 
   * @throws ApiError
   */
  public static apiV1EntitiesList(
contentType?: Array<'cmrrisk' | 'legalentity' | 'person' | 'property' | 'vehicle'>,
excludeIds?: string,
identifier?: string,
ids?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedAPIEntityList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/entities/',
      query: {
        'content_type': contentType,
        'exclude_ids': excludeIds,
        'identifier': identifier,
        'ids': ids,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * Detalii entitate
   * Returnează o înregistrare individuală din registrul comun de entități.
   * @param id 
   * @returns APIEntity 
   * @throws ApiError
   */
  public static apiV1EntitiesRetrieve(
id: number,
): CancelablePromise<APIEntity> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/entities/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Listare entități juridice
   * Returnează entitățile juridice disponibile în registrul de entități. Poate fi folosit pentru identificarea companiilor care apar ca și contractant sau proprietar.
   * @param country 
   * @param excludeIds comma separated list
   * @param identificationNumber 
   * @param ids comma separated list
   * @param name 
   * @param ordering Ordering fields: created, modified, name
   * @param owner Filtrează entitățile juridice după ID-ul utilizatorului proprietar.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name, identification_number
   * @returns PaginatedAPILegalEntityListList 
   * @throws ApiError
   */
  public static apiV1LegalEntitiesList(
country?: string,
excludeIds?: string,
identificationNumber?: string,
ids?: string,
name?: string,
ordering?: string,
owner?: number,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedAPILegalEntityListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/legal-entities/',
      query: {
        'country': country,
        'exclude_ids': excludeIds,
        'identification_number': identificationNumber,
        'ids': ids,
        'name': name,
        'ordering': ordering,
        'owner': owner,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * Detalii entitate juridică
   * Returnează datele detaliate ale unei entități juridice, inclusiv datele fiscale și de contact disponibile.
   * @param id 
   * @returns APILegalEntityDetail 
   * @throws ApiError
   */
  public static apiV1LegalEntitiesRetrieve(
id: number,
): CancelablePromise<APILegalEntityDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/legal-entities/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Listare persoane fizice
   * Returnează persoanele fizice disponibile în registrul de entități. Poate fi folosit pentru identificarea contractantului, proprietarului sau persoanei asigurate.
   * @param country 
   * @param dateOfBirth 
   * @param excludeIds comma separated list
   * @param gender * `female` - Female
 * * `male` - Male
   * @param identificationNumber 
   * @param ids comma separated list
   * @param ordering Ordering fields: created, modified
   * @param owner Filtrează persoanele după ID-ul utilizatorului proprietar.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: identification_number, first_name, last_name
   * @returns PaginatedAPIPersonListList 
   * @throws ApiError
   */
  public static apiV1PersonsList(
country?: string,
dateOfBirth?: string,
excludeIds?: string,
gender?: 'female' | 'male' | null,
identificationNumber?: string,
ids?: string,
ordering?: string,
owner?: number,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedAPIPersonListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/persons/',
      query: {
        'country': country,
        'date_of_birth': dateOfBirth,
        'exclude_ids': excludeIds,
        'gender': gender,
        'identification_number': identificationNumber,
        'ids': ids,
        'ordering': ordering,
        'owner': owner,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * Detalii persoană fizică
   * Returnează datele detaliate pentru o persoană fizică, inclusiv identificatorii și documentele disponibile.
   * @param id 
   * @returns APIPersonDetail 
   * @throws ApiError
   */
  public static apiV1PersonsRetrieve(
id: number,
): CancelablePromise<APIPersonDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/v1/persons/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param ordering Ordering fields: name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name
   * @returns PaginatedColorList 
   * @throws ApiError
   */
  public static entitiesColorsList(
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedColorList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/colors/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns Color 
   * @throws ApiError
   */
  public static entitiesColorsCreate(
requestBody: Color,
): CancelablePromise<Color> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/entities/colors/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param name A unique value identifying this color.
   * @returns Color 
   * @throws ApiError
   */
  public static entitiesColorsRetrieve(
name: string,
): CancelablePromise<Color> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/colors/{name}/',
      path: {
        'name': name,
      },
    });
  }

  /**
   * @param name A unique value identifying this color.
   * @param requestBody 
   * @returns Color 
   * @throws ApiError
   */
  public static entitiesColorsPartialUpdate(
name: string,
requestBody?: PatchedColor,
): CancelablePromise<Color> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/entities/colors/{name}/',
      path: {
        'name': name,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param name A unique value identifying this color.
   * @returns void 
   * @throws ApiError
   */
  public static entitiesColorsDestroy(
name: string,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/entities/colors/{name}/',
      path: {
        'name': name,
      },
    });
  }

  /**
   * Insurance entities
   * @param contentType Filtrează entitățile după tip: persoană, entitate juridică sau vehicul.
 *
 * * `person` - Person
 * * `legalentity` - Legal Entity
 * * `vehicle` - Vehicle
 * * `property` - Property
 * * `cmrrisk` - Cmr Risk
   * @param excludeIds comma separated list
   * @param identifier Potrivire exactă pentru IDNP, IDNO sau numărul certificatului vehiculului.
   * @param ids comma separated list
   * @param ordering Ordering fields: id, object__name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: as_person__first_name, as_person__last_name, as_person__identification_number, as_legalentity__name, as_legalentity__identification_number, as_vehicle__model__name, as_vehicle__brand__name, as_vehicle__certificate_number, as_vehicle__registration_number, as_property__description
   * @returns PaginatedEntityList 
   * @throws ApiError
   */
  public static entitiesEntitiesList(
contentType?: Array<'cmrrisk' | 'legalentity' | 'person' | 'property' | 'vehicle'>,
excludeIds?: string,
identifier?: string,
ids?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedEntityList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/entities/',
      query: {
        'content_type': contentType,
        'exclude_ids': excludeIds,
        'identifier': identifier,
        'ids': ids,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * Insurance entities
   * @param id A unique integer value identifying this entity.
   * @returns Entity 
   * @throws ApiError
   */
  public static entitiesEntitiesRetrieve(
id: number,
): CancelablePromise<Entity> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/entities/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param country 
   * @param excludeIds comma separated list
   * @param identificationNumber 
   * @param ids comma separated list
   * @param name 
   * @param ordering Ordering fields: created, modified, name
   * @param owner Filtrează entitățile juridice după ID-ul utilizatorului proprietar.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name, identification_number
   * @returns PaginatedLegalEntityListList 
   * @throws ApiError
   */
  public static entitiesLegalEntitiesList(
country?: string,
excludeIds?: string,
identificationNumber?: string,
ids?: string,
name?: string,
ordering?: string,
owner?: number,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedLegalEntityListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/legal-entities/',
      query: {
        'country': country,
        'exclude_ids': excludeIds,
        'identification_number': identificationNumber,
        'ids': ids,
        'name': name,
        'ordering': ordering,
        'owner': owner,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns LegalEntity 
   * @throws ApiError
   */
  public static entitiesLegalEntitiesCreate(
requestBody: LegalEntity,
): CancelablePromise<LegalEntity> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/entities/legal-entities/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this legal entity.
   * @returns LegalEntityDetail 
   * @throws ApiError
   */
  public static entitiesLegalEntitiesRetrieve(
id: number,
): CancelablePromise<LegalEntityDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/legal-entities/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this legal entity.
   * @param requestBody 
   * @returns LegalEntity 
   * @throws ApiError
   */
  public static entitiesLegalEntitiesPartialUpdate(
id: number,
requestBody?: PatchedLegalEntity,
): CancelablePromise<LegalEntity> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/entities/legal-entities/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this legal entity.
   * @returns void 
   * @throws ApiError
   */
  public static entitiesLegalEntitiesDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/entities/legal-entities/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param country 
   * @param dateOfBirth 
   * @param excludeIds comma separated list
   * @param gender * `female` - Female
 * * `male` - Male
   * @param identificationNumber 
   * @param ids comma separated list
   * @param ordering Ordering fields: created, modified
   * @param owner Filtrează persoanele după ID-ul utilizatorului proprietar.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: identification_number, first_name, last_name
   * @returns PaginatedPersonListList 
   * @throws ApiError
   */
  public static entitiesPersonsList(
country?: string,
dateOfBirth?: string,
excludeIds?: string,
gender?: 'female' | 'male' | null,
identificationNumber?: string,
ids?: string,
ordering?: string,
owner?: number,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedPersonListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/persons/',
      query: {
        'country': country,
        'date_of_birth': dateOfBirth,
        'exclude_ids': excludeIds,
        'gender': gender,
        'identification_number': identificationNumber,
        'ids': ids,
        'ordering': ordering,
        'owner': owner,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns Person 
   * @throws ApiError
   */
  public static entitiesPersonsCreate(
requestBody: Person,
): CancelablePromise<Person> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/entities/persons/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this person.
   * @returns PersonDetail 
   * @throws ApiError
   */
  public static entitiesPersonsRetrieve(
id: number,
): CancelablePromise<PersonDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/persons/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this person.
   * @param requestBody 
   * @returns Person 
   * @throws ApiError
   */
  public static entitiesPersonsPartialUpdate(
id: number,
requestBody?: PatchedPerson,
): CancelablePromise<Person> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/entities/persons/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this person.
   * @returns void 
   * @throws ApiError
   */
  public static entitiesPersonsDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/entities/persons/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Example file for imports
   * @returns binary 
   * @throws ApiError
   */
  public static entitiesPersonsImportCsvTemplateRetrieve(): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/persons/import-csv-template/',
    });
  }

  /**
   * Example file for imports
   * @returns binary 
   * @throws ApiError
   */
  public static entitiesPersonsImportXlsxTemplateRetrieve(): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/persons/import-xlsx-template/',
    });
  }

  /**
   * @param category * `A` - Clădiri și construcții
 * * `B` - Finisajul încăperilor
 * * `C` - Vitrine, vitralii și afișele de reclamă
 * * `D` - Aparate electrice și tehnică electronică
 * * `E` - Mobilă, inventar de producție și gospodărie
 * * `F` - Mărfuri și materiale la depozit
 * * `G` - Utilaj de producție
 * * `H` - Mărfuri și materiale din săli comerciale
 * * `I` - Altele
 * * `J` - Terenuri
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param ordering Ordering fields: created, modified, name
   * @param owner Filtrează proprietățile după ID-ul utilizatorului proprietar.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name
   * @returns PaginatedPropertyListList 
   * @throws ApiError
   */
  public static entitiesPropertiesList(
category?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'I' | 'J',
excludeIds?: string,
ids?: string,
ordering?: string,
owner?: number,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedPropertyListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/properties/',
      query: {
        'category': category,
        'exclude_ids': excludeIds,
        'ids': ids,
        'ordering': ordering,
        'owner': owner,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this property.
   * @returns PropertyDetail 
   * @throws ApiError
   */
  public static entitiesPropertiesRetrieve(
id: number,
): CancelablePromise<PropertyDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/properties/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param country 
   * @param excludeIds comma separated list
   * @param identificationNumber 
   * @param ids comma separated list
   * @param name 
   * @param ordering Ordering fields: created, modified, name
   * @param owner Filtrează entitățile juridice după ID-ul utilizatorului proprietar.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name, identification_number
   * @returns PaginatedUserLegalEntityList 
   * @throws ApiError
   */
  public static entitiesUserLegalEntitiesList(
country?: string,
excludeIds?: string,
identificationNumber?: string,
ids?: string,
name?: string,
ordering?: string,
owner?: number,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedUserLegalEntityList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/user-legal-entities/',
      query: {
        'country': country,
        'exclude_ids': excludeIds,
        'identification_number': identificationNumber,
        'ids': ids,
        'name': name,
        'ordering': ordering,
        'owner': owner,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * Upsert by identifier logic
   * @param requestBody 
   * @returns UserLegalEntity 
   * @throws ApiError
   */
  public static entitiesUserLegalEntitiesCreate(
requestBody: UserLegalEntity,
): CancelablePromise<UserLegalEntity> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/entities/user-legal-entities/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this legal entity.
   * @returns UserLegalEntity 
   * @throws ApiError
   */
  public static entitiesUserLegalEntitiesRetrieve(
id: number,
): CancelablePromise<UserLegalEntity> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/user-legal-entities/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this legal entity.
   * @returns void 
   * @throws ApiError
   */
  public static entitiesUserLegalEntitiesDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/entities/user-legal-entities/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param country 
   * @param dateOfBirth 
   * @param excludeIds comma separated list
   * @param gender * `female` - Female
 * * `male` - Male
   * @param identificationNumber 
   * @param ids comma separated list
   * @param ordering Ordering fields: created, modified
   * @param owner Filtrează persoanele după ID-ul utilizatorului proprietar.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: identification_number, first_name, last_name
   * @returns PaginatedUserPersonList 
   * @throws ApiError
   */
  public static entitiesUserPersonsList(
country?: string,
dateOfBirth?: string,
excludeIds?: string,
gender?: 'female' | 'male' | null,
identificationNumber?: string,
ids?: string,
ordering?: string,
owner?: number,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedUserPersonList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/user-persons/',
      query: {
        'country': country,
        'date_of_birth': dateOfBirth,
        'exclude_ids': excludeIds,
        'gender': gender,
        'identification_number': identificationNumber,
        'ids': ids,
        'ordering': ordering,
        'owner': owner,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * Upsert by identifier logic
   * @param requestBody 
   * @returns UserPerson 
   * @throws ApiError
   */
  public static entitiesUserPersonsCreate(
requestBody: UserPerson,
): CancelablePromise<UserPerson> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/entities/user-persons/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this person.
   * @returns UserPerson 
   * @throws ApiError
   */
  public static entitiesUserPersonsRetrieve(
id: number,
): CancelablePromise<UserPerson> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/user-persons/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this person.
   * @param requestBody 
   * @returns UserPerson 
   * @throws ApiError
   */
  public static entitiesUserPersonsPartialUpdate(
id: number,
requestBody?: PatchedUserPerson,
): CancelablePromise<UserPerson> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/entities/user-persons/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this person.
   * @returns void 
   * @throws ApiError
   */
  public static entitiesUserPersonsDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/entities/user-persons/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param brand 
   * @param certificateNumber 
   * @param country 
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param model 
   * @param ordering Ordering fields: created, modified, model, brand, year
   * @param owner Filtrează vehiculele după ID-ul utilizatorului proprietar.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param registrationNumber 
   * @param search Search fields: brand__name, registration_number, model__name, certificate_number, vin, idnv
   * @param type * `vehicle` - Vehicle
 * * `trailer` - Trailer
   * @param vin 
   * @param year 
   * @returns PaginatedUserVehicleList 
   * @throws ApiError
   */
  public static entitiesUserVehiclesList(
brand?: string,
certificateNumber?: string,
country?: string,
excludeIds?: string,
ids?: string,
model?: string,
ordering?: string,
owner?: number,
page?: number,
pageSize?: number,
registrationNumber?: string,
search?: string,
type?: 'trailer' | 'vehicle',
vin?: string,
year?: number,
): CancelablePromise<PaginatedUserVehicleList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/user-vehicles/',
      query: {
        'brand': brand,
        'certificate_number': certificateNumber,
        'country': country,
        'exclude_ids': excludeIds,
        'ids': ids,
        'model': model,
        'ordering': ordering,
        'owner': owner,
        'page': page,
        'page_size': pageSize,
        'registration_number': registrationNumber,
        'search': search,
        'type': type,
        'vin': vin,
        'year': year,
      },
    });
  }

  /**
   * Upsert by identifier logic
   * @param requestBody 
   * @returns UserVehicle 
   * @throws ApiError
   */
  public static entitiesUserVehiclesCreate(
requestBody: UserVehicle,
): CancelablePromise<UserVehicle> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/entities/user-vehicles/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this vehicle.
   * @returns UserVehicle 
   * @throws ApiError
   */
  public static entitiesUserVehiclesRetrieve(
id: number,
): CancelablePromise<UserVehicle> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/user-vehicles/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this vehicle.
   * @returns void 
   * @throws ApiError
   */
  public static entitiesUserVehiclesDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/entities/user-vehicles/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Validare live a unui câmp
   * Stateless, computation-only field validation (no DB reads/writes) — public on purpose,
 * same AllowAny pattern as ProductViewSet's "price" action, since mobile calls this during
 * the pre-submit quote form, before the user may be authenticated.
   * @param requestBody 
   * @returns FieldValidationOutput 
   * @throws ApiError
   */
  public static entitiesValidateFieldCreate(
requestBody?: FieldValidationRequest,
): CancelablePromise<FieldValidationOutput> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/entities/validate-field/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param isChina 
   * @param isPremium 
   * @param ordering Ordering fields: name, is_premium, is_china
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name
   * @returns PaginatedVehicleBrandList 
   * @throws ApiError
   */
  public static entitiesVehicleBrandsList(
excludeIds?: string,
ids?: string,
isChina?: boolean,
isPremium?: boolean,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedVehicleBrandList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/vehicle-brands/',
      query: {
        'exclude_ids': excludeIds,
        'ids': ids,
        'is_china': isChina,
        'is_premium': isPremium,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns VehicleBrand 
   * @throws ApiError
   */
  public static entitiesVehicleBrandsCreate(
requestBody: VehicleBrand,
): CancelablePromise<VehicleBrand> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/entities/vehicle-brands/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param name A unique value identifying this vehicle brand.
   * @returns VehicleBrand 
   * @throws ApiError
   */
  public static entitiesVehicleBrandsRetrieve(
name: string,
): CancelablePromise<VehicleBrand> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/vehicle-brands/{name}/',
      path: {
        'name': name,
      },
    });
  }

  /**
   * @param name A unique value identifying this vehicle brand.
   * @param requestBody 
   * @returns VehicleBrand 
   * @throws ApiError
   */
  public static entitiesVehicleBrandsPartialUpdate(
name: string,
requestBody?: PatchedVehicleBrand,
): CancelablePromise<VehicleBrand> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/entities/vehicle-brands/{name}/',
      path: {
        'name': name,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param name A unique value identifying this vehicle brand.
   * @returns void 
   * @throws ApiError
   */
  public static entitiesVehicleBrandsDestroy(
name: string,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/entities/vehicle-brands/{name}/',
      path: {
        'name': name,
      },
    });
  }

  /**
   * @param brand Filtrează modelele de vehicul după ID-ul mărcii.
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param ordering Ordering fields: name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name
   * @returns PaginatedVehicleModelList 
   * @throws ApiError
   */
  public static entitiesVehicleModelsList(
brand?: string,
excludeIds?: string,
ids?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedVehicleModelList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/vehicle-models/',
      query: {
        'brand': brand,
        'exclude_ids': excludeIds,
        'ids': ids,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns VehicleModel 
   * @throws ApiError
   */
  public static entitiesVehicleModelsCreate(
requestBody: VehicleModel,
): CancelablePromise<VehicleModel> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/entities/vehicle-models/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param name A unique value identifying this vehicle model.
   * @returns VehicleModel 
   * @throws ApiError
   */
  public static entitiesVehicleModelsRetrieve(
name: string,
): CancelablePromise<VehicleModel> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/vehicle-models/{name}/',
      path: {
        'name': name,
      },
    });
  }

  /**
   * @param name A unique value identifying this vehicle model.
   * @param requestBody 
   * @returns VehicleModel 
   * @throws ApiError
   */
  public static entitiesVehicleModelsPartialUpdate(
name: string,
requestBody?: PatchedVehicleModel,
): CancelablePromise<VehicleModel> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/entities/vehicle-models/{name}/',
      path: {
        'name': name,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param name A unique value identifying this vehicle model.
   * @returns void 
   * @throws ApiError
   */
  public static entitiesVehicleModelsDestroy(
name: string,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/entities/vehicle-models/{name}/',
      path: {
        'name': name,
      },
    });
  }

  /**
   * @param brand 
   * @param certificateNumber 
   * @param country 
   * @param excludeIds comma separated list
   * @param ids comma separated list
   * @param model 
   * @param ordering Ordering fields: created, modified, model, brand, year
   * @param owner Filtrează vehiculele după ID-ul utilizatorului proprietar.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param registrationNumber 
   * @param search Search fields: brand__name, registration_number, model__name, certificate_number, vin, idnv
   * @param type * `vehicle` - Vehicle
 * * `trailer` - Trailer
   * @param vin 
   * @param year 
   * @returns PaginatedVehicleListList 
   * @throws ApiError
   */
  public static entitiesVehiclesList(
brand?: string,
certificateNumber?: string,
country?: string,
excludeIds?: string,
ids?: string,
model?: string,
ordering?: string,
owner?: number,
page?: number,
pageSize?: number,
registrationNumber?: string,
search?: string,
type?: 'trailer' | 'vehicle',
vin?: string,
year?: number,
): CancelablePromise<PaginatedVehicleListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/vehicles/',
      query: {
        'brand': brand,
        'certificate_number': certificateNumber,
        'country': country,
        'exclude_ids': excludeIds,
        'ids': ids,
        'model': model,
        'ordering': ordering,
        'owner': owner,
        'page': page,
        'page_size': pageSize,
        'registration_number': registrationNumber,
        'search': search,
        'type': type,
        'vin': vin,
        'year': year,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns Vehicle 
   * @throws ApiError
   */
  public static entitiesVehiclesCreate(
requestBody: Vehicle,
): CancelablePromise<Vehicle> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/entities/vehicles/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this vehicle.
   * @returns VehicleDetail 
   * @throws ApiError
   */
  public static entitiesVehiclesRetrieve(
id: number,
): CancelablePromise<VehicleDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/entities/vehicles/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this vehicle.
   * @param requestBody 
   * @returns Vehicle 
   * @throws ApiError
   */
  public static entitiesVehiclesPartialUpdate(
id: number,
requestBody?: PatchedVehicle,
): CancelablePromise<Vehicle> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/entities/vehicles/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param id A unique integer value identifying this vehicle.
   * @returns void 
   * @throws ApiError
   */
  public static entitiesVehiclesDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/entities/vehicles/{id}/',
      path: {
        'id': id,
      },
    });
  }

}
